export type ProgrammeSlide = {
  src: string;
  alt: string;
  caption: string;
  width: number;
  height: number;
};

export type GalleryCollection = {
  slug: string;
  programmeAnchor: string;
  title: string;
  description: string;
  slides: ProgrammeSlide[];
  kind?: "temple";
};

/**
 * Programme photographs are deliberately separate from programme copy so that
 * galleries can be updated without editing page components. Keep captions
 * factual and provide useful alternative text for every image.
 */
export const galleryCollections: GalleryCollection[] = [
  {
    slug: "mahathee-veda-pathashala",
    programmeAnchor: "page-4",
    title: "Mahathee Veda Pāṭhaśālā",
    description: "Photographs of students, traditional learning and spiritual practice at the Pāṭhaśālā.",
    slides: [
    {
      src: "/programmes/mahathee-veda-pathashala/students-group-2025.jpg",
      alt: "Veda Pāṭhaśālā students seated together with learning materials",
      caption: "Students of the Veda Pāṭhaśālā",
      width: 1280,
      height: 960,
    },
    {
      src: "/programmes/mahathee-veda-pathashala/teaching-session-2025.jpg",
      alt: "Veda students seated during a teaching session",
      caption: "A teaching session at the Pāṭhaśālā",
      width: 1599,
      height: 906,
    },
    {
      src: "/programmes/mahathee-veda-pathashala/vedic-ritual-2026.jpg",
      alt: "Students and priests gathered around a sacred fire during a Vedic ritual",
      caption: "Students gathered for a Vedic fire ritual",
      width: 1599,
      height: 899,
    },
    ],
  },
  {
    slug: "school-donations",
    programmeAnchor: "page-5",
    title: "School Donations",
    description: "Photographs of school supplies being given to children, alongside a published report on the 2026 educational kit distribution.",
    slides: [
    {
      src: "/programmes/school-donations/student-receiving-supplies-2025.jpg",
      alt: "A schoolchild receiving a bag and learning materials from adults",
      caption: "A schoolchild receives learning materials",
      width: 1600,
      height: 1200,
    },
    {
      src: "/programmes/school-donations/children-with-school-bags-2025.jpg",
      alt: "Schoolchildren gathered outdoors holding bags of school supplies",
      caption: "Children gathered with school supply bags",
      width: 1160,
      height: 868,
    },
    {
      src: "/programmes/school-donations/student-receiving-bag-2025.jpg",
      alt: "A child receiving a school supply bag while other children watch",
      caption: "A schoolchild receives a supply bag",
      width: 960,
      height: 1280,
    },
    {
      src: "/programmes/school-donations/student-presentation-2025.jpg",
      alt: "Adults presenting a school supply bag to a student in uniform",
      caption: "Learning materials presented to a student",
      width: 960,
      height: 1280,
    },
    {
      src: "/programmes/school-donations/schoolchildren-gathering-2023.jpg",
      alt: "A large group of schoolchildren gathered outdoors with adults seated in front",
      caption: "Schoolchildren gathered outdoors",
      width: 1600,
      height: 899,
    },
    {
      src: "/programmes/school-donations/educational-kit-report-2026.jpg",
      alt: "Newspaper report showing schoolchildren at a Sivasakthi Ashram educational kit distribution",
      caption: "Report on the 2026 educational kit distribution",
      width: 960,
      height: 1600,
    },
    ],
  },
  {
    slug: "sivasakthi-ashram",
    programmeAnchor: "page-8",
    title: "Sivasakthi Ashram",
    description: "Photographs of the temple, its flower-adorned shrines, fire rituals, processions and gatherings at Kailasa Konai.",
    kind: "temple",
    slides: [
      {
        src: "/temple/fire-offering-2026.jpg",
        alt: "Women and men seated around a sacred fire inside the temple",
        caption: "A fire offering at the ashram",
        width: 1280,
        height: 960,
      },
      {
        src: "/temple/decorated-procession-2026.jpg",
        alt: "A flower-decorated procession beneath a red ceremonial canopy at night",
        caption: "A flower-decorated procession",
        width: 1280,
        height: 960,
      },
      {
        src: "/temple/flower-adorned-sannidhi-2026.jpg",
        alt: "A shrine richly adorned with pink, white and yellow flowers",
        caption: "A flower-adorned sannidhi",
        width: 720,
        height: 1280,
      },
      {
        src: "/temple/fire-ritual-2025.jpg",
        alt: "A group gathered around a sacred fire with offerings",
        caption: "Gathered around the sacred fire",
        width: 1280,
        height: 720,
      },
      {
        src: "/temple/offerings-at-fire-2025.jpg",
        alt: "Women and men making offerings at a sacred fire",
        caption: "Offerings at the sacred fire",
        width: 1600,
        height: 899,
      },
      {
        src: "/temple/devotees-at-ashram-2025.jpg",
        alt: "Devotees of different ages gathered at the ashram entrance",
        caption: "Devotees gathered at the ashram",
        width: 1280,
        height: 960,
      },
      {
        src: "/temple/decorated-shrine-2024.jpg",
        alt: "A flower-decorated shrine with a garlanded portrait above it",
        caption: "A decorated shrine at the ashram",
        width: 960,
        height: 1280,
      },
      {
        src: "/temple/devotees-in-procession-2024.jpg",
        alt: "Devotees walking in procession along the temple grounds",
        caption: "A procession on the temple grounds",
        width: 1600,
        height: 1200,
      },
      {
        src: "/temple/flower-adorned-shiva-shrine-2025.jpg",
        alt: "A Shiva lingam and other icons surrounded by colorful flower garlands",
        caption: "A flower-adorned shrine",
        width: 1200,
        height: 1600,
      },
    ],
  },
];

export const programmeGalleries: Record<string, ProgrammeSlide[]> = {
  "page-3": [],
  "page-4": galleryCollections[0].slides,
  "evening-pathashala": [],
  "page-5": galleryCollections[1].slides,
};

export function galleryHrefForProgramme(anchor: string) {
  const collection = galleryCollections.find((item) => item.programmeAnchor === anchor);
  return collection ? `/gallery/#${collection.slug}` : undefined;
}
